import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Users, Shield } from 'lucide-react';
import { Button } from './ui/button';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Nos Assurances', path: '/services' },
    { name: 'Assurance 24/7', path: '/assurance-temporaire' },
    { name: 'Carte Grise', path: '/carte-grise' },
    { name: 'Partenaires', path: '/partenaires' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-slate-900/5 py-3' 
          : 'bg-transparent py-5'
      }`}
    >
      {/* Top bar - only when not scrolled */}
      {!isScrolled && (
        <div className="hidden lg:block absolute top-0 left-0 right-0 bg-[#0F2137] text-white text-sm py-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-2">
                <Shield size={14} className="text-[#C5A059]" />
                <span className="text-slate-300">ORIAS n° 24000879</span>
              </span>
              <span className="text-slate-500">|</span>
              <span className="text-slate-300">46 bd Pierre Delbrel, 82200 Moissac</span>
            </div>
            <a href="tel:0582819120" className="flex items-center gap-2 text-[#C5A059] hover:text-white transition-colors">
              <Phone size={14} />
              <span className="font-medium">05 82 81 91 20</span>
            </a>
          </div>
        </div>
      )}

      <nav className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${!isScrolled ? 'mt-10 lg:mt-8' : ''}`}>
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img 
              src="/logo-cambc.png" 
              alt="Cabinet CAMBC Conseils" 
              className={`h-12 w-auto transition-all duration-300 ${
                isScrolled ? '' : 'brightness-0 invert'
              }`}
            />
            <div className="hidden sm:block">
              <span className={`font-bold text-xl tracking-tight transition-colors duration-300 ${
                isScrolled ? 'text-[#0F2137]' : 'text-white'
              }`}>
                CAMBC
              </span>
              <span className={`block text-xs tracking-[0.15em] uppercase transition-colors duration-300 ${
                isScrolled ? 'text-[#C5A059]' : 'text-white/70'
              }`}>
                Conseils
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  location.pathname === link.path 
                    ? isScrolled 
                      ? 'text-[#0F2137] bg-slate-100' 
                      : 'text-white bg-white/10'
                    : isScrolled 
                      ? 'text-slate-600 hover:text-[#0F2137] hover:bg-slate-50' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-4">
            <Link 
              to="/login" 
              data-testid="nav-link-espace-apporteur"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                isScrolled 
                  ? 'text-slate-600 hover:text-[#0F2137] hover:bg-slate-50' 
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <Users size={16} />
              Espace Apporteur
            </Link>
            <Link to="/quote">
              <Button 
                data-testid="nav-cta-devis"
                className="bg-[#C5A059] hover:bg-[#B08A45] text-white font-semibold px-6 py-2.5 rounded-lg shadow-lg shadow-[#C5A059]/25 transition-all duration-300 hover:shadow-xl hover:shadow-[#C5A059]/30 hover:-translate-y-0.5"
              >
                Obtenir un Devis
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isScrolled 
                ? 'text-slate-700 hover:bg-slate-100' 
                : 'text-white hover:bg-white/10'
            }`}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t border-slate-100 animate-in slide-in-from-top-2 duration-200">
            <div className="p-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.path 
                      ? 'text-[#0F2137] bg-[#F5EFE6]' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              <div className="pt-4 mt-4 border-t border-slate-100 space-y-3">
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 py-3 px-4 rounded-lg text-sm font-medium text-[#C5A059] bg-[#F5EFE6]"
                >
                  <Users size={16} />
                  Espace Apporteur
                </Link>
                <Link to="/quote" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#C5A059] hover:bg-[#B08A45] text-white font-semibold py-3 rounded-lg">
                    Obtenir un Devis Gratuit
                  </Button>
                </Link>
                <a href="tel:0582819120" className="flex items-center justify-center gap-2 py-3 text-[#0F2137] font-medium">
                  <Phone size={16} />
                  05 82 81 91 20
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
